// src/pages/ProductsPage.js
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { PRODUCT_CATEGORIES } from "../data/products";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { getPublicCategories } from "../services/categories.api";
import { useSeo } from "../hooks/useSeo";
import { apiRequest } from "../services/api";
import {
  getProductsPageFeaturedSection,
  getHomepageCategoryProductRows,
} from "../services/homepage.api";
import {
  getListingState,
  saveListingState,
  restoreProductReturnPosition,
  saveProductReturnPosition,
} from "../utils/navigationMemory";

function SortChevronIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M6.5 9L12 14.5L17.5 9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ScrollTopArrowIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M12 20V5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M5.5 11.5L12 5L18.5 11.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProductListingSkeleton({ lang }) {
  const skeletonCards = Array.from({ length: 12 });

  return (
    <div
      className="w-full min-h-screen flex flex-col overflow-x-hidden bg-white"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

      {/* MOBILE SKELETON */}
      <div className="md:hidden w-full bg-white pt-[5px] px-4 pb-24">
        <div className="mb-4">
          <div className="h-4 w-[210px] bg-gray-200 animate-pulse rounded" />
        </div>

        <div className="flex items-center justify-center gap-4 mb-3">
          <div className="h-6 w-[150px] bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-[70px] bg-gray-200 animate-pulse rounded" />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="h-[44px] bg-gray-200 animate-pulse rounded" />
          <div className="h-[44px] bg-gray-200 animate-pulse rounded" />
        </div>

        <div className="mb-5 flex gap-4 overflow-hidden px-7">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="shrink-0 min-w-[110px] h-[40px] bg-gray-200 animate-pulse rounded"
            />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-8">
          {skeletonCards.slice(0, 6).map((_, index) => (
            <div key={index} className="w-full">
              <div className="w-full aspect-[3/4] bg-gray-200 animate-pulse" />
              <div className="mt-3 h-4 w-[90%] bg-gray-200 animate-pulse rounded" />
              <div className="mt-2 h-4 w-[55%] bg-gray-200 animate-pulse rounded" />
              <div className="mt-3 flex gap-1.5">
                <div className="h-[22px] w-[30px] bg-gray-200 animate-pulse rounded" />
                <div className="h-[22px] w-[30px] bg-gray-200 animate-pulse rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* DESKTOP SKELETON */}
      <div className="hidden md:block w-full bg-white px-8 pt-6 pb-10 mt-[175px]">
        <aside
          className="
            fixed left-8 top-[210px] z-30
            w-[calc(20vw-32px)]
            pr-4 border-r border-[#e5e5e5]
            bg-white
          "
        >
          <div className="h-4 w-[170px] bg-gray-200 animate-pulse rounded mb-8" />

          <div className="h-6 w-[110px] bg-gray-200 animate-pulse rounded mb-4" />
          <div className="space-y-3 mb-8">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-4 w-[75%] bg-gray-200 animate-pulse rounded" />
            ))}
          </div>

          <div className="h-6 w-[90px] bg-gray-200 animate-pulse rounded mb-5" />
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i}>
                <div className="h-5 w-[80px] bg-gray-200 animate-pulse rounded mb-3" />
                <div className="grid grid-cols-2 gap-2">
                  <div className="h-4 bg-gray-200 animate-pulse rounded" />
                  <div className="h-4 bg-gray-200 animate-pulse rounded" />
                  <div className="h-4 bg-gray-200 animate-pulse rounded" />
                  <div className="h-4 bg-gray-200 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        </aside>

        <main className="ml-[20%] pl-6 min-w-0">
          <div className="flex items-center justify-between gap-6 mb-5">
            <div className="flex items-center gap-3">
              <div className="h-6 w-[230px] bg-gray-200 animate-pulse rounded" />
              <div className="h-4 w-[90px] bg-gray-200 animate-pulse rounded" />
            </div>

            <div className="h-[42px] w-[245px] bg-gray-200 animate-pulse rounded" />
          </div>

          <div className="relative mb-7">
            <div className="flex gap-3 px-7 overflow-hidden">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="shrink-0 min-w-[120px] h-[38px] bg-gray-200 animate-pulse rounded"
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-x-6 gap-y-9">
            {skeletonCards.map((_, index) => (
              <div key={index} className="w-full">
                <div className="w-full aspect-[3/4] bg-gray-200 animate-pulse" />
                <div className="mt-4 h-4 w-[85%] bg-gray-200 animate-pulse rounded" />
                <div className="mt-2 h-4 w-[45%] bg-gray-200 animate-pulse rounded" />
                <div className="mt-3 flex justify-end gap-1.5">
                  <div className="h-[22px] w-[30px] bg-gray-200 animate-pulse rounded" />
                  <div className="h-[22px] w-[30px] bg-gray-200 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>

      <Footer className="mt-auto" />
    </div>
  );
}

function ProductEmptyState({ lang, text, onClear, onShopAll }) {
  return (
    <div
      className="
        w-full
        min-h-[260px]
        md:min-h-[360px]
        flex items-center justify-center
        px-4 py-14 md:py-20
        bg-white
      "
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <div className="max-w-[520px] mx-auto text-center">
        <div
          className="
            mx-auto
            w-[62px] h-[62px]
            rounded-full
            border border-black
            flex items-center justify-center
            text-[30px]
            font-light
            text-black
          "
        >
          !
        </div>

        <h2 className="mt-5 text-[24px] md:text-[30px] leading-tight font-extrabold tracking-[0.08em] uppercase text-black">
          {lang === "AR" ? "لا توجد منتجات" : "No products found"}
        </h2>

        <p className="mt-3 text-[14px] md:text-[15px] leading-relaxed text-[#666]">
          {text}
        </p>

        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={onClear}
            className="
              w-full sm:w-[190px]
              h-[46px]
              border border-black
              bg-white
              text-black
              text-[14px]
              font-semibold
              uppercase
              tracking-[0.06em]
              hover:bg-[#f7f7f7]
              active:scale-[0.99]
              transition
            "
          >
            {lang === "AR" ? "مسح الفلاتر" : "Clear filters"}
          </button>

          <button
            type="button"
            onClick={onShopAll}
            className="
              w-full sm:w-[190px]
              h-[46px]
              bg-black
              text-white
              text-[14px]
              font-semibold
              uppercase
              tracking-[0.06em]
              hover:opacity-90
              active:scale-[0.99]
              transition
            "
          >
            {lang === "AR" ? "كل المنتجات" : "Shop all"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductsPage() {
  const { lang } = useContext(LanguageContext);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [canViewPrices, setCanViewPrices] = useState(() =>
  Boolean(localStorage.getItem("esill-token"))
);

const [publicCollectionProductIds, setPublicCollectionProductIds] = useState([]);

  const isIOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const isAndroid =
    typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

  const isSafari =
    typeof navigator !== "undefined" &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const SPECIAL_SECTIONS = ["new-in", "bestsellers", "sale"];
  const urlCategory = searchParams.get("category");
const urlSelectedCategory = searchParams.get("selectedCategory");
const urlTag = searchParams.get("tag");
const skipNextUrlWriteRef = useRef(false);

  const [baseSpecialSection, setBaseSpecialSection] = useState(() => {
  const section = urlCategory || location.state?.section || null;
  return SPECIAL_SECTIONS.includes(section) ? section : null;
});

const [initialSection, setInitialSection] = useState(() => {
  const section = urlCategory || location.state?.section || null;

  if (SPECIAL_SECTIONS.includes(section)) {
    return urlSelectedCategory || null;
  }

  return section;
});

  const [productsPageFeatured, setProductsPageFeatured] = useState(null);

  const [isDesktopView, setIsDesktopView] = useState(() =>
  typeof window !== "undefined" ? window.innerWidth >= 768 : false
);

React.useEffect(() => {
  const updateDesktopView = () => {
    setIsDesktopView(window.innerWidth >= 768);
  };

  updateDesktopView();
  window.addEventListener("resize", updateDesktopView);

  return () => window.removeEventListener("resize", updateDesktopView);
}, []);

const getAdaptivePageSize = () => {
  if (typeof window === "undefined") return 20;

  const width = window.innerWidth;
  const memory = navigator.deviceMemory || 4;

  // ✅ Desktop / tablets
  if (width >= 1280) return 30;
  if (width >= 768) return memory <= 4 ? 20 : 24;

  // ✅ Mobile
  if (isIOS && isSafari) return 12;
  if (isIOS) return 14;
  if (isAndroid && memory <= 4) return 14;
  if (isAndroid) return 16;

  return 16;
};

const [pageSize, setPageSize] = useState(() => getAdaptivePageSize());

React.useEffect(() => {
  const updatePageSize = () => {
    setPageSize(getAdaptivePageSize());
  };

  updatePageSize();
  window.addEventListener("resize", updatePageSize);

  return () => window.removeEventListener("resize", updatePageSize);
}, [isIOS, isSafari, isAndroid]);

const PAGE_SIZE = pageSize;

  const getValidPageNumber = (value) => {
    const parsedPage = Number(value || 1);

    return Number.isFinite(parsedPage) && parsedPage > 0
      ? Math.floor(parsedPage)
      : 1;
  };

  const pageParam = searchParams.get("page");
  const pageFromUrl = getValidPageNumber(pageParam);

  const savedListingStateRef = useRef(getListingState());

  const [currentPage, setCurrentPage] = useState(() => {
    if (pageParam) return pageFromUrl;

    return getValidPageNumber(
      savedListingStateRef.current?.currentPage
    );
  });

  const didMountFiltersRef = useRef(false);

  React.useEffect(() => {
    if (!pageParam) return;

    setCurrentPage((previousPage) =>
      previousPage === pageFromUrl ? previousPage : pageFromUrl
    );
  }, [pageParam, pageFromUrl]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const seoItems = useMemo(() => {
    return products.slice(0, 20);
  }, [products]);


  React.useEffect(() => {
  const trackKey = `esill-enter-products:${location.pathname}${location.search}`;

  if (sessionStorage.getItem(trackKey)) return;

  sessionStorage.setItem(trackKey, "1");

  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_products",
      entity_type: "page",
    }),
  }).catch(() => {});
}, [location.pathname, location.search]);

  const searchFromUrl = searchParams.get("search");
  const searchQuery = searchFromUrl || location.state?.search || null;

  const [allCategories, setAllCategories] = useState([]);
  const [allVariantProducts, setAllVariantProducts] = useState([]);


  // ✅ Scroll-to-top button visibility
  const [showScrollTop, setShowScrollTop] = useState(false);


  React.useEffect(() => {
    setAllVariantProducts(products);
  }, [products]);

  // FILTER STATE
  const [categoryFilter, setCategoryFilter] = useState(() => {
  const section = urlCategory || location.state?.section || null;

  const selectedCategory = SPECIAL_SECTIONS.includes(section)
    ? urlSelectedCategory
    : section;

  if (selectedCategory && !SPECIAL_SECTIONS.includes(selectedCategory) && !searchQuery) {
    return [selectedCategory];
  }

  return [];
});

  React.useEffect(() => {
  getProductsPageFeaturedSection()
    .then(setProductsPageFeatured)
    .catch(() => {});
}, []);

React.useEffect(() => {
  let mounted = true;

  getHomepageCategoryProductRows()
    .then((rows) => {
      if (!mounted) return;

      const ids = new Set();

      (Array.isArray(rows) ? rows : []).forEach((row) => {
        (Array.isArray(row.products) ? row.products : []).forEach((product) => {
          if (product?.id) {
            ids.add(Number(product.id));
          }
        });
      });

      setPublicCollectionProductIds(Array.from(ids));
    })
    .catch(() => {
      if (mounted) setPublicCollectionProductIds([]);
    });

  return () => {
    mounted = false;
  };
}, []);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);

    const filters = {};

    // ✅ SEARCH
    if (searchQuery) {
      filters.search = searchQuery;
    }

    if (baseSpecialSection === "new-in") {
  filters.new = true;
}

if (baseSpecialSection === "bestsellers") {
  filters.bestseller = true;
}

if (baseSpecialSection === "sale") {
  filters.sale = true;
}

    /* ✅ CATEGORY FILTER (disabled during search) */
    if (!searchQuery && categoryFilter.length > 0) {
  filters.category = categoryFilter[0];
}

    getAllProducts(filters)
      .then((data) => {
        if (mounted) {
          setProducts(data.map(mapBackendProduct));
        }
      })
      .catch(console.error)
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [baseSpecialSection, initialSection, searchQuery, categoryFilter]);

 React.useEffect(() => {
  if (location.state?.section) {
    const section = location.state.section;

    if (SPECIAL_SECTIONS.includes(section)) {
      setBaseSpecialSection(section);
      setInitialSection(null);
      setCategoryFilter([]);
    } else {
      setBaseSpecialSection(null);
      setInitialSection(section);
      setCategoryFilter([section]);
    }

    setSelectedTag(null);
    setColorFilter([]);
  }
}, [location.state?.section, location.state?.forceRefresh]);

  const [colorFilter, setColorFilter] = useState([]);

  const [selectedTag, setSelectedTag] = useState(urlTag || null);

  const [sizeFilter, setSizeFilter] = useState([]);
const [onlyAvailable, setOnlyAvailable] = useState(false);

const [desktopSortOpen, setDesktopSortOpen] = useState(false);

const DESKTOP_SIDEBAR_TOP = 210;
const [desktopSidebarHeight, setDesktopSidebarHeight] = useState(
  `calc(100vh - ${DESKTOP_SIDEBAR_TOP}px)`
);
const [desktopSidebarVisible, setDesktopSidebarVisible] = useState(true);

const publicCollectionProductIdSet = useMemo(
  () => new Set(publicCollectionProductIds.map(Number)),
  [publicCollectionProductIds]
);

const [sizeSectionOpen, setSizeSectionOpen] = useState(true);
const [colorSectionOpen, setColorSectionOpen] = useState(true);
const [priceSectionOpen, setPriceSectionOpen] = useState(true);
const [desktopCategoriesExpanded, setDesktopCategoriesExpanded] = useState(false);
const desktopCategoryRailRef = useRef(null);

  // SORT + UI
  const [sortOption, setSortOption] = useState("bestSelling");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [gridMode, setGridMode] = useState("two");

  const [priceMin, setPriceMin] = useState(0);
  const PRICE_LIMIT = 500;

const [priceMax, setPriceMax] = useState(PRICE_LIMIT);

const priceSliderRef = useRef(null);
const [draggingPriceThumb, setDraggingPriceThumb] = useState(null);

useEffect(() => {
  const syncAuthState = () => {
    setCanViewPrices(Boolean(localStorage.getItem("esill-token")));
  };

  syncAuthState();

  window.addEventListener("auth-changed", syncAuthState);
  return () => window.removeEventListener("auth-changed", syncAuthState);
}, []);

useEffect(() => {
  if (!canViewPrices && (sortOption === "priceLowHigh" || sortOption === "priceHighLow")) {
    setSortOption("bestSelling");
  }
}, [canViewPrices, sortOption]);

React.useEffect(() => {
  if (!location.state?.resetFiltersFromHeader) return;

  // ✅ Stop the old state from writing back to the URL once.
  // This prevents summer <-> pants vibration.
  skipNextUrlWriteRef.current = true;

  const headerCategory = searchParams.get("category");
  const isSpecial = SPECIAL_SECTIONS.includes(headerCategory);

  if (isSpecial) {
    setBaseSpecialSection(headerCategory);
    setInitialSection(null);
    setCategoryFilter([]);
  } else if (headerCategory) {
    setBaseSpecialSection(null);
    setInitialSection(headerCategory);
    setCategoryFilter([headerCategory]);
  } else {
    setBaseSpecialSection(null);
    setInitialSection(null);
    setCategoryFilter([]);
  }

  setSelectedTag(null);
  setColorFilter([]);
  setSizeFilter([]);
  setOnlyAvailable(false);
  setPriceMin(0);
  setPriceMax(PRICE_LIMIT);
  setSortOption("bestSelling");
  setCurrentPage(1);
  setIsFilterOpen(false);
  setIsSortOpen(false);
  setDesktopSortOpen(false);

  if (desktopCategoryRailRef.current) {
    desktopCategoryRailRef.current.scrollTo({
      left: 0,
      behavior: "auto",
    });
  }
}, [location.state?.resetKey]);

  // PRICE RANGE
  const maxProductPrice = useMemo(() => {
    return products.reduce((max, p) => {
      const finalPrice = p.discount
        ? Math.round(p.price * (1 - p.discount / 100))
        : p.price;
      return finalPrice > max ? finalPrice : max;
    }, 0);
  }, [products]);

 React.useEffect(() => {
  setPriceMax((prev) => Math.min(prev, PRICE_LIMIT));
}, []);



  // ✅ Show scroll-to-top button after scrolling
  React.useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    getPublicCategories().then(setAllCategories).catch(console.error);
  }, []);

  React.useEffect(() => {
  if (searchQuery) return;

  if (!urlCategory) {
    setBaseSpecialSection(null);
    setInitialSection(null);
    setCategoryFilter([]);
    setSelectedTag(urlTag || null);
    return;
  }

  if (SPECIAL_SECTIONS.includes(urlCategory)) {
    setBaseSpecialSection(urlCategory);

    const selectedCategory = urlSelectedCategory || null;
    setInitialSection(selectedCategory);
    setCategoryFilter(selectedCategory ? [selectedCategory] : []);
  } else {
    setBaseSpecialSection(null);
    setInitialSection(urlCategory);
    setCategoryFilter([urlCategory]);
  }

  setSelectedTag(urlTag || null);
}, [urlCategory, urlSelectedCategory, urlTag, searchQuery]);

React.useEffect(() => {
  if (searchQuery) return;

  // ✅ Header navigation already changed the URL.
  // Skip one old-state URL write to prevent category bouncing.
  if (skipNextUrlWriteRef.current) {
    skipNextUrlWriteRef.current = false;
    return;
  }

  const params = new URLSearchParams(searchParams);

  if (baseSpecialSection) {
    params.set("category", baseSpecialSection);

    if (initialSection) {
      params.set("selectedCategory", initialSection);
    } else {
      params.delete("selectedCategory");
    }
  } else if (initialSection) {
    params.set("category", initialSection);
    params.delete("selectedCategory");
  } else {
    params.delete("category");
    params.delete("selectedCategory");
  }

  if (selectedTag) {
    params.set("tag", selectedTag);
  } else {
    params.delete("tag");
  }

  const next = params.toString();
  const current = searchParams.toString();

  if (next !== current) {
    setSearchParams(params, { replace: true });
  }
}, [
  baseSpecialSection,
  initialSection,
  selectedTag,
  searchQuery,
  searchParams,
  setSearchParams,
]);

  const TEXT = {
    EN: {
      titleBySection: {
        "new-in": "New In",
        bestsellers: "Bestsellers",
        sale: "Season Sale",
        [PRODUCT_CATEGORIES.HEELS]: "Heels",
        [PRODUCT_CATEGORIES.SNEAKERS]: "Sneakers",
        [PRODUCT_CATEGORIES.BOOTS]: "Boots",
        [PRODUCT_CATEGORIES.FLATS_LOAFERS]: "Flats & Loafers",
        [PRODUCT_CATEGORIES.SANDALS]: "Sandals",
        [PRODUCT_CATEGORIES.ACCESSORIES]: "Bags & Accessories",
      },
      filters: "Filters",
      sortBy: "Sort by",
      bestSelling: "Best Selling",
      priceLowHigh: "Price: Low to High",
      priceHighLow: "Price: High to Low",
      productsWord: "products",
      category: "Category",
      size: "Size",
      color: "Color",
      price: "Price",
      heelHeight: "Heel height",
      all: "All",
      low: "Low",
      mid: "Mid",
      high: "High",
      appliedFilters: "Applied filters",
      clear: "Clear",
      clearAll: "Clear filters",
      apply: "Apply",
      min: "Min",
      max: "Max",
      noProducts: "No products found in this selection.",
    },
    AR: {
      titleBySection: {
        "new-in": "وصل حديثاً",
        bestsellers: "الأكثر مبيعاً",
        sale: "تخفيضات الموسم",
        [PRODUCT_CATEGORIES.HEELS]: "أحذية كعب",
        [PRODUCT_CATEGORIES.SNEAKERS]: "أحذية رياضية",
        [PRODUCT_CATEGORIES.BOOTS]: "بوت",
        [PRODUCT_CATEGORIES.FLATS_LOAFERS]: "فلات ولوفر",
        [PRODUCT_CATEGORIES.SANDALS]: "صنادل",
        [PRODUCT_CATEGORIES.ACCESSORIES]: "حقائب وإكسسوارات",
      },
      filters: "تصفية",
      sortBy: "ترتيب حسب",
      bestSelling: "الأكثر مبيعاً",
      priceLowHigh: "السعر: من الأقل للأعلى",
      priceHighLow: "السعر: من الأعلى للأقل",
      productsWord: "منتج",
      category: "الفئة",
      size: "المقاس",
      color: "اللون",
      price: "السعر",
      heelHeight: "ارتفاع الكعب",
      all: "الكل",
      low: "منخفض",
      mid: "متوسط",
      high: "عالٍ",
      appliedFilters: "الفلاتر المفعّلة",
      clear: "مسح",
      clearAll: "مسح الكل",
      apply: "تطبيق",
      min: "الحد الأدنى",
      max: "الحد الأقصى",
      noProducts: "لا توجد منتجات ضمن هذه التصفية.",
    },
  }[lang];

  const selectedCategoryTitle =
  allCategories.find((c) => c.slug === initialSection)?.[
    lang === "EN" ? "name_en" : "name_ar"
  ] || TEXT.titleBySection[initialSection];

const baseSpecialTitle =
  baseSpecialSection ? TEXT.titleBySection[baseSpecialSection] : null;

const pageTitle =
  selectedCategoryTitle ||
  baseSpecialTitle ||
  TEXT.titleBySection["new-in"];

  const finalTitle = searchQuery ? `"${searchQuery}"` : pageTitle;

  // CATEGORY OPTIONS (for drawer)
  const CATEGORY_OPTIONS = allCategories.map((c) => ({
    key: c.slug,
    labelEN: c.name_en,
    labelAR: c.name_ar,
  }));

  // AVAILABLE SIZES / COLORS
  const allSizes = useMemo(() => {
    const set = new Set();

    allVariantProducts.forEach((p) =>
      p.sizes?.forEach(({ size }) => {
        if (size === null || size === undefined) return;

        // block real NaN number
        if (typeof size === "number" && Number.isNaN(size)) return;

        // block empty strings
        if (typeof size === "string" && size.trim() === "") return;

        set.add(size);
      })
    );

    const arr = Array.from(set);

    // keep your smart sort
    return arr.sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isNaN(na) || Number.isNaN(nb))
        return String(a).localeCompare(String(b));
      return na - nb;
    });
  }, [allVariantProducts]);

  const allColors = useMemo(() => {
    const c = [];
    allVariantProducts.forEach((p) =>
      p.colors?.forEach((col) => {
        if (!c.find((x) => x.name === col.name)) c.push(col);
      })
    );
    return c;
  }, [allVariantProducts]);

  const productHasAvailableStock = (product) => {
  const directStock =
    Number(product.totalStock ?? product.total_stock ?? product.stock ?? 0);

  if (directStock > 0) return true;

  const sizeStock = product.sizes?.some((item) => {
    if (typeof item === "object") {
      return Number(item.stock ?? item.quantity ?? item.qty ?? 0) > 0;
    }

    return false;
  });

  return Boolean(sizeStock);
};

const productMatchesSize = (product, selectedSizes) => {
  if (!selectedSizes.length) return true;

  return product.sizes?.some((item) => {
    const value =
      typeof item === "object"
        ? item.size ?? item.name ?? item.label
        : item;

    return selectedSizes.includes(String(value));
  });
};

const truncateSortLabel = (label) => {
  const safe = String(label || "");
  return safe.length > 11 ? `${safe.slice(0, 11)}...` : safe;
};

const resolveProductImage = (url) => {
  if (!url) return "/images/placeholder-product.jpg";
  if (String(url).startsWith("http")) return url;

  const base =
    process.env.REACT_APP_API_URL?.replace(/\/api$/, "") ||
    window.location.origin;

  return `${base}${url}`;
};

const filterState = useMemo(
  () => ({
    baseSpecialSection,
    initialSection,
    categoryFilter,
    selectedTag,
    colorFilter,
    sizeFilter,
    onlyAvailable,
    priceMin,
    priceMax,
    sortOption,
  }),
  [
  baseSpecialSection,
  initialSection,
  categoryFilter,
  selectedTag,
  colorFilter,
  sizeFilter,
  onlyAvailable,
  priceMin,
  priceMax,
  sortOption,
]
);

  // MAIN FILTERING + SORTING
  const filteredAndSorted = useMemo(() => {
    let result = [...products];

    if (!canViewPrices && publicCollectionProductIds.length > 0) {
  result = result.filter((p) =>
    publicCollectionProductIdSet.has(Number(p.id))
  );
}

    const {
  categoryFilter,
  selectedTag,
  colorFilter,
  sizeFilter,
  onlyAvailable,
  priceMin,
  priceMax,
  sortOption,
} = filterState;

    // Category filter (disabled during search unless user explicitly filters)
    if (!searchQuery && categoryFilter.length > 0) {
      result = result.filter((p) =>
        p.categories?.some((c) => categoryFilter.includes(c))
      );
    }

    // Tag filter
if (selectedTag) {
  result = result.filter((p) => {
    const tags = Array.isArray(p.tags)
      ? p.tags
      : String(p.tags || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);

    return tags.includes(selectedTag);
  });
}

    // Color
    if (colorFilter.length) {
      result = result.filter((p) =>
        p.colors?.some((c) => colorFilter.includes(c.name))
      );
    }

    // Size
if (sizeFilter.length) {
  result = result.filter((p) => productMatchesSize(p, sizeFilter));
}

// Availability
if (onlyAvailable) {
  result = result.filter((p) => productHasAvailableStock(p));
}

    // Price range - only for logged-in users
if (canViewPrices && priceMax !== null) {
  result = result.filter((p) => {
    const finalPrice = p.discount
      ? Math.round(p.price * (1 - p.discount / 100))
      : p.price;
    return finalPrice >= priceMin && finalPrice <= priceMax;
  });
}

    // SORTING
    result = [...result];
    if (canViewPrices && sortOption === "priceLowHigh") {
      result.sort((a, b) => {
        const pa = a.discount ? a.price * (1 - a.discount / 100) : a.price;
        const pb = b.discount ? b.price * (1 - b.discount / 100) : b.price;
        return pa - pb;
      });
    } else if (canViewPrices && sortOption === "priceHighLow") {
      result.sort((a, b) => {
        const pa = a.discount ? a.price * (1 - a.discount / 100) : a.price;
        const pb = b.discount ? b.price * (1 - b.discount / 100) : b.price;
        return pb - pa;
      });
    } else {
      // "bestSelling" – prefer new items but keep base order
      result.sort((a, b) => (b.newIndex || 0) - (a.newIndex || 0));
    }

    return result;
  }, [
  products,
  filterState,
  searchQuery,
  canViewPrices,
  publicCollectionProductIds.length,
  publicCollectionProductIdSet,
]);

const seoEntityId = searchQuery
  ? null
  : baseSpecialSection ||
    (initialSection && !SPECIAL_SECTIONS.includes(initialSection)
      ? initialSection
      : null);

useSeo(
  searchQuery ? "search" : "category",
  seoEntityId,
  filteredAndSorted.length
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": searchQuery ? "SearchResultsPage" : "CollectionPage",
            "@id": window.location.href,
            name: searchQuery
  ? `Search results for "${searchQuery}" | ETIKA`
  : `${finalTitle} | ETIKA Lebanon`,
            url: window.location.href,
            description: searchQuery
  ? `Browse search results for "${searchQuery}" at ETIKA Lebanon. Explore wholesale women’s clothing, fashion pieces, and new arrivals for boutiques and retailers.`
  : `Shop ${finalTitle} at ETIKA Lebanon. Discover wholesale women’s clothing, stylish fashion pieces, new arrivals, and seasonal collections for boutiques and retailers.`,
          },
          {
            "@type": "ItemList",
            name: finalTitle,
            itemListElement: filteredAndSorted
              .slice(0, 20)
              .map((p, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: `${window.location.origin}/product/${p.slug}`,
                name: lang === "EN" ? p.nameEN : p.nameAR,
              })),
          },
        ],
      }
    : null
);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / PAGE_SIZE));

  React.useEffect(() => {
  if (loading) return;

  if (currentPage > totalPages) {
    const safePage = totalPages;
    const params = new URLSearchParams(searchParams);

    if (safePage > 1) {
      params.set("page", String(safePage));
    } else {
      params.delete("page");
    }

    setCurrentPage(safePage);
    setSearchParams(params, { replace: true });
  }
}, [
  loading,
  currentPage,
  totalPages,
  searchParams,
  setSearchParams,
]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredAndSorted.slice(start, end);
  }, [filteredAndSorted, currentPage, PAGE_SIZE]);

  React.useEffect(() => {
  if (loading) return;

  const timer = setTimeout(() => {
    restoreProductReturnPosition();
  }, 80);

  return () => clearTimeout(timer);
}, [loading, currentPage, paginatedProducts.length]);

  const filterResetSignature = JSON.stringify({
    baseSpecialSection,
    initialSection,
    categoryFilter,
    selectedTag,
    colorFilter,
    sizeFilter,
    onlyAvailable,
    priceMin,
    priceMax,
    sortOption,
    searchQuery,
  });

  const previousFilterResetSignatureRef = useRef(filterResetSignature);
  const filterResetReadyRef = useRef(false);

  React.useEffect(() => {
    if (loading) {
      filterResetReadyRef.current = false;
      return;
    }

    previousFilterResetSignatureRef.current = filterResetSignature;

    const timer = window.setTimeout(() => {
      previousFilterResetSignatureRef.current = filterResetSignature;
      filterResetReadyRef.current = true;
    }, 0);

    return () => window.clearTimeout(timer);
  }, [loading, filterResetSignature]);

  React.useEffect(() => {
    if (!filterResetReadyRef.current) {
      previousFilterResetSignatureRef.current = filterResetSignature;
      return;
    }

    if (
      previousFilterResetSignatureRef.current === filterResetSignature
    ) {
      return;
    }

    previousFilterResetSignatureRef.current = filterResetSignature;

    const params = new URLSearchParams(window.location.search);
    params.delete("page");

    setCurrentPage(1);
    setSearchParams(params, { replace: true });
  }, [filterResetSignature, setSearchParams]);

  const showHeelFilter =
    initialSection === PRODUCT_CATEGORIES.HEELS || initialSection === "new-in";

  // GRID CLASSES
  const gridClasses =
    gridMode === "two"
      ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

  // HELPERS
  const toggleCategory = (key) => {
    setCategoryFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const clearAllFilters = () => {
  setInitialSection(null);
  setCategoryFilter([]);
  setSelectedTag(null);
  setColorFilter([]);
  setSizeFilter([]);
  setOnlyAvailable(false);
  setPriceMin(0);
  setPriceMax(PRICE_LIMIT);
};

 
// Applied filters (for chips)
const appliedFilters = [];

if (onlyAvailable) {
  appliedFilters.push({
    id: "available",
    label: lang === "EN" ? "Only available" : "المتوفر فقط",
    onClear: () => setOnlyAvailable(false),
  });
}

sizeFilter.forEach((size) => {
  appliedFilters.push({
    id: `size-${size}`,
    label: String(size),
    onClear: () =>
      setSizeFilter((prev) => prev.filter((item) => item !== size)),
  });
});

colorFilter.forEach((color) => {
  appliedFilters.push({
    id: `color-${color}`,
    label: String(color),
    onClear: () =>
      setColorFilter((prev) => prev.filter((item) => item !== color)),
  });
});


if (canViewPrices && (priceMin > 0 || priceMax < PRICE_LIMIT)) {
  appliedFilters.push({
    id: "price",
    label: `$${Number(priceMin || 0).toFixed(0)} - $${Number(priceMax || PRICE_LIMIT).toFixed(0)}`,
    onClear: () => {
      setPriceMin(0);
      setPriceMax(PRICE_LIMIT);
    },
  });
}

const productCount = Number(filteredAndSorted.length) || 0;

    const categoryRailRef = useRef(null);

  const scrollCategoryRail = (direction) => {
    if (!categoryRailRef.current) return;

    const amount = 150;
    categoryRailRef.current.scrollBy({
      left: direction === "next" ? amount : -amount,
      behavior: "smooth",
    });
  };

  const scrollDesktopCategoryRail = (direction) => {
  if (!desktopCategoryRailRef.current) return;

  desktopCategoryRailRef.current.scrollBy({
    left: direction === "next" ? 180 : -180,
    behavior: "smooth",
  });
};

const clampDesktopPrice = (value) => {
  const n = Number(value || 0);
  return Math.max(0, Math.min(PRICE_LIMIT, n));
};

const visibleDesktopCategories = desktopCategoriesExpanded
  ? CATEGORY_OPTIONS
  : CATEGORY_OPTIONS.slice(0, 5);

  const getSortLabel = () => {
    if (sortOption === "priceLowHigh") return TEXT.priceLowHigh;
    if (sortOption === "priceHighLow") return TEXT.priceHighLow;
    return TEXT.bestSelling;
  };

  const getDesktopBreadcrumbTitle = () => {
  if (searchQuery) return finalTitle;

  const raw = String(pageTitle || finalTitle || "").trim();

  if (lang === "EN") {
    if (raw.toLowerCase() === "summer") return "Summer products";
    if (raw.toLowerCase().includes("products")) return raw;
    return `${raw} products`;
  }

  return raw;
};

  const paginationItems = useMemo(() => {
    if (totalPages <= 0) return [];

    const visiblePageCount = Math.min(4, totalPages);

    // Keep the current page near the middle:
    // current page 6 => 4, 5, 6, 7
    let startPage = currentPage - 2;

    // Beginning boundary:
    // page 1, 2 or 3 => 1, 2, 3, 4
    startPage = Math.max(1, startPage);

    // Ending boundary:
    // near the final page => last four available pages
    const maximumStartPage = Math.max(
      1,
      totalPages - visiblePageCount + 1
    );

    startPage = Math.min(startPage, maximumStartPage);

    return Array.from(
      { length: visiblePageCount },
      (_, index) => startPage + index
    );
  }, [currentPage, totalPages]);

const goToPage = (page) => {
  const safePage = Math.max(
    1,
    Math.min(totalPages, getValidPageNumber(page))
  );

  if (safePage === currentPage) return;

  const params = new URLSearchParams(searchParams);

  if (safePage > 1) {
    params.set("page", String(safePage));
  } else {
    params.delete("page");
  }

  setCurrentPage(safePage);
  setSearchParams(params, { replace: false });

  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
};

  React.useEffect(() => {
  if (loading) return;

  saveListingState({
    currentPage,
  });
}, [loading, currentPage, location.pathname, location.search]);

  const updatePriceFromPointer = (clientX, thumb) => {
  const slider = priceSliderRef.current;
  if (!slider) return;

  const rect = slider.getBoundingClientRect();
  const rawPercent = (clientX - rect.left) / rect.width;
  const clampedPercent = Math.min(1, Math.max(0, rawPercent));
  const rawValue = Math.round(clampedPercent * PRICE_LIMIT);

  if (thumb === "min") {
    setPriceMin(Math.min(rawValue, priceMax - 1));
    return;
  }

  if (thumb === "max") {
    setPriceMax(Math.max(rawValue, priceMin + 1));
  }
};

const stopPriceDragging = () => {
  setDraggingPriceThumb(null);
};

React.useEffect(() => {
  if (!draggingPriceThumb) return;

  const onMove = (event) => {
    updatePriceFromPointer(event.clientX, draggingPriceThumb);
  };

  const onTouchMove = (event) => {
    updatePriceFromPointer(event.touches[0].clientX, draggingPriceThumb);
  };

  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", stopPriceDragging);
  window.addEventListener("touchmove", onTouchMove);
  window.addEventListener("touchend", stopPriceDragging);

  return () => {
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", stopPriceDragging);
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("touchend", stopPriceDragging);
  };
}, [draggingPriceThumb, priceMin, priceMax]);

React.useEffect(() => {
  if (!isDesktopView) return;

  const handleDesktopSidebarLimit = () => {
    const footer = document.querySelector("footer");

    const normalHeight = window.innerHeight - DESKTOP_SIDEBAR_TOP;

    if (!footer) {
      setDesktopSidebarHeight(`${normalHeight}px`);
      setDesktopSidebarVisible(true);
      return;
    }

    const footerTop = footer.getBoundingClientRect().top;

    // Distance from fixed sidebar top to the footer boundary
    const availableHeightBeforeFooter = footerTop - DESKTOP_SIDEBAR_TOP;

    const nextHeight = Math.min(normalHeight, availableHeightBeforeFooter);
    const safeHeight = Math.max(0, nextHeight);

    setDesktopSidebarHeight(`${safeHeight}px`);
    setDesktopSidebarVisible(safeHeight > 40);
  };

  handleDesktopSidebarLimit();

  window.addEventListener("scroll", handleDesktopSidebarLimit);
  window.addEventListener("resize", handleDesktopSidebarLimit);

  return () => {
    window.removeEventListener("scroll", handleDesktopSidebarLimit);
    window.removeEventListener("resize", handleDesktopSidebarLimit);
  };
}, [isDesktopView]);

const isCategorySelectedForTagRow =
  !searchQuery &&
  initialSection &&
  !SPECIAL_SECTIONS.includes(initialSection);

const getProductTags = (product) => {
  if (Array.isArray(product.tags)) {
    return product.tags.map(String).filter(Boolean);
  }

  return String(product.tags || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const categoryTagOptions = useMemo(() => {
  if (!isCategorySelectedForTagRow) return [];

  const counts = new Map();

  products.forEach((product) => {
    const belongsToCategory =
      product.categories?.includes(initialSection) ||
      product.primary_category === initialSection;

    if (!belongsToCategory) return;

    getProductTags(product).forEach((tag) => {
      counts.set(tag, (counts.get(tag) || 0) + 1);
    });
  });

  let tags = Array.from(counts.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));

  if (selectedTag) {
    tags = [
      ...tags.filter((item) => item.tag === selectedTag),
      ...tags.filter((item) => item.tag !== selectedTag),
    ];
  }

  return tags;
}, [products, initialSection, selectedTag, isCategorySelectedForTagRow]);

const getCategoryLabel = (slug) => {
  const cat = allCategories.find((c) => c.slug === slug);

  if (!cat) return slug;

  return lang === "AR" ? cat.name_ar : cat.name_en;
};

const getSpecialLabel = (section) => {
  return TEXT.titleBySection[section] || section;
};

const resetToBaseCollection = () => {
  setInitialSection(null);
  setCategoryFilter([]);
  setSelectedTag(null);
  setColorFilter([]);
  setSizeFilter([]);
  setOnlyAvailable(false);
  setPriceMin(0);
  setPriceMax(PRICE_LIMIT);
};

const goToBreadcrumbCategory = () => {
  setSelectedTag(null);
};

const breadcrumbItems = [
  {
    id: "home",
    label: lang === "EN" ? "Main page" : "الصفحة الرئيسية",
    onClick: () => navigate("/"),
  },
];

if (baseSpecialSection) {
  breadcrumbItems.push({
    id: "base-special",
    label: getSpecialLabel(baseSpecialSection),
    onClick: resetToBaseCollection,
  });
}

if (!baseSpecialSection && initialSection) {
  breadcrumbItems.push({
    id: "category-base",
    label: getCategoryLabel(initialSection),
    onClick: () => {
      setSelectedTag(null);
    },
  });
}

if (baseSpecialSection && initialSection) {
  breadcrumbItems.push({
    id: "category-selected",
    label: getCategoryLabel(initialSection),
    onClick: goToBreadcrumbCategory,
  });
}

if (selectedTag) {
  breadcrumbItems.push({
    id: "tag-selected",
    label: selectedTag,
    onClick: () => setSelectedTag(null),
  });
}

  if (loading) {
  return <ProductListingSkeleton lang={lang} />;
}

  return (
    <div
      className="
        w-full min-h-screen flex flex-col overflow-x-hidden
        bg-white 
      "
    >
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

            {/* PAGE CONTENT */}
      <div
  className="md:hidden w-full min-h-screen bg-white pt-[5px] px-4 pb-24"
  dir={lang === "AR" ? "rtl" : "ltr"}
>
        {/* MOBILE BREADCRUMB */}
        <ProductsBreadcrumb
  items={breadcrumbItems}
  lang={lang}
  variant="mobile"
/>

        {/* MOBILE TITLE + COUNT */}
        <div className="md:hidden flex items-center justify-center gap-4 mb-2">
          <h1 className="text-[18px] font-extrabold tracking-[0.16em] uppercase text-black">
            {String(finalTitle || "").toUpperCase()}
          </h1>

          <span className="text-[13px] tracking-[0.12em] text-black whitespace-nowrap">
            {String(productCount)} {TEXT.productsWord}
          </span>
        </div>

        {/* MOBILE FILTER + SORT */}
        <div className="md:hidden grid grid-cols-2 gap-4 mb-4">
          <button
            onClick={() => setIsFilterOpen(true)}
            className="
              h-[44px] bg-black text-white
              flex items-center justify-center gap-2
              text-[16px] font-semibold tracking-[0.08em]
              uppercase active:scale-[0.99]
            "
          >
            {TEXT.filters}
            <span className="relative w-5 h-5 inline-flex items-center justify-center">
              <span className="absolute w-5 h-[1.5px] bg-white top-[4px]" />
              <span className="absolute w-5 h-[1.5px] bg-white top-[10px]" />
              <span className="absolute w-5 h-[1.5px] bg-white top-[16px]" />
              <span className="absolute w-[5px] h-[5px] rounded-full border border-white bg-black top-[1px] left-[3px]" />
              <span className="absolute w-[5px] h-[5px] rounded-full border border-white bg-black top-[7px] right-[3px]" />
              <span className="absolute w-[5px] h-[5px] rounded-full border border-white bg-black top-[13px] left-[9px]" />
            </span>
          </button>

          <div className="relative">
            <button
              onClick={() => setIsSortOpen((p) => !p)}
              className="
                h-[44px] w-full bg-white border border-black
                flex items-center justify-between
                px-4 text-[15px] text-black
                active:scale-[0.99]
              "
            >
              <span className="min-w-0 flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap">
  {TEXT.sortBy}:{truncateSortLabel(getSortLabel())}
</span>
              <SortChevronIcon
  className={`
    w-[18px] h-[18px]
    text-black
    transition-transform duration-200
    ${isSortOpen ? "rotate-180" : ""}
  `}
/>
            </button>

           {isSortOpen && (
  <div className="absolute left-0 right-0 top-[62px] z-30 bg-white border border-black shadow-lg">
    <button
      onClick={() => {
        setSortOption("bestSelling");
        setIsSortOpen(false);
      }}
      className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50"
    >
      {TEXT.bestSelling}
    </button>

    {canViewPrices && (
      <>
        <button
          onClick={() => {
            setSortOption("priceLowHigh");
            setIsSortOpen(false);
          }}
          className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50"
        >
          {TEXT.priceLowHigh}
        </button>

        <button
          onClick={() => {
            setSortOption("priceHighLow");
            setIsSortOpen(false);
          }}
          className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50"
        >
          {TEXT.priceHighLow}
        </button>
      </>
    )}
  </div>
)}
          </div>
        </div>

{/* MOBILE APPLIED FILTER CHIPS */}
{appliedFilters.length > 0 && (
  <div className="md:hidden mb-4">
    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
      {appliedFilters.map((filter) => (
        <button
          key={filter.id}
          onClick={filter.onClear}
          className="
            shrink-0
            min-w-[34px]
            h-[34px]
            px-3
            border border-black
            bg-white
            text-[13px] text-black
            flex items-center justify-center gap-2
            whitespace-nowrap
          "
        >
          <span>{filter.label}</span>
          <span className="text-[18px] font-bold text-gray-700 leading-none">
            ×
          </span>
        </button>
      ))}

      <button
        onClick={clearAllFilters}
        className="
          shrink-0
          h-[34px]
          px-3
          bg-gray-800
          text-[13px] text-white
          whitespace-nowrap
        "
      >
        {TEXT.clearAll}
      </button>
    </div>
  </div>
)}

        {/* MOBILE CATEGORY RAIL */}
        <div className="md:hidden relative mb-5">
          <button
            onClick={() => scrollCategoryRail("prev")}
            className="
              absolute left-0 -top-1 z-10 h-[52px] w-7
              bg-white/90 text-black text-2xl
              flex items-center justify-center
            "
            aria-label="Previous categories"
          >
            ‹
          </button>

          <div
            ref={categoryRailRef}
            className="
              flex gap-4 overflow-x-auto scroll-smooth
              px-7 no-scrollbar
            "
          >
            {isCategorySelectedForTagRow && categoryTagOptions.length > 0
  ? categoryTagOptions.map((item, index) => {
      const active = selectedTag === item.tag;
      const palette = [
        "bg-[#FDE8E8]",
        "bg-[#F7F7D9]",
        "bg-[#F8F5FF]",
        "bg-[#EAF7F1]",
        "bg-[#FFF0D9]",
        "bg-[#EAF2FF]",
      ];

      return (
        <button
          key={item.tag}
          onClick={() =>
            setSelectedTag((prev) => (prev === item.tag ? null : item.tag))
          }
          className={`
            shrink-0 min-w-[110px] h-[40px]
            px-5 flex items-center justify-center
            text-[14px] text-black whitespace-nowrap
            ${palette[index % palette.length]}
            ${active ? "font-extrabold" : "font-normal"}
          `}
        >
          {item.tag}
        </button>
      );
    })
  : CATEGORY_OPTIONS.map((c, index) => {
      const palette = [
        "bg-[#FDE8E8]",
        "bg-[#F7F7D9]",
        "bg-[#F8F5FF]",
        "bg-[#EAF7F1]",
        "bg-[#FFF0D9]",
        "bg-[#EAF2FF]",
      ];

      return (
        <button
          key={c.key}
          onClick={() => {
            setSelectedTag(null);
            setCategoryFilter([c.key]);
            setInitialSection(c.key);
          }}
          className={`
            shrink-0 min-w-[110px] h-[40px]
            px-5 flex items-center justify-center
            text-[14px] text-black whitespace-nowrap
            ${palette[index % palette.length]}
          `}
        >
          {lang === "EN" ? c.labelEN : c.labelAR}
        </button>
      );
    })}
          </div>

          <button
            onClick={() => scrollCategoryRail("next")}
            className="
              absolute right-0 -top-1 z-10 h-[52px] w-7
              bg-white/90 text-black text-2xl
              flex items-center justify-center
            "
            aria-label="Next categories"
          >
            ›
          </button>
        </div>

        {/* DESKTOP OLD HEADER - HIDDEN FOR NOW */}
        <div className="hidden md:block">
          <h1
            className="
              text-2xl md:text-3xl font-extrabold text-center mt-2
              bg-gradient-to-r from-[#F97316] via-[#8B5CF6] to-[#2563EB]
              bg-clip-text text-transparent
            "
          >
            {String(finalTitle || "").toUpperCase()}
          </h1>
          <div
            className="
              h-1 w-20 mx-auto mt-3 mb-6 rounded-full
              bg-gradient-to-r from-[#F97316] via-[#8B5CF6] to-[#2563EB]
            "
          />
        </div>

        {/* PRODUCTS / EMPTY STATE */}
{filteredAndSorted.length === 0 ? (
  <ProductEmptyState
    lang={lang}
    text={TEXT.noProducts}
    onClear={clearAllFilters}
    onShopAll={() => navigate("/all-products")}
  />
) : (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8 md:gap-6">
    {!isDesktopView &&
      paginatedProducts.map((p, index) => (
        <ProductCard
          key={p.id}
          product={p}
          imagePriority={index < 4}
        />
      ))}
  </div>
)}
      </div>

{/* DESKTOP PAGE CONTENT */}
<div
  className="hidden md:block w-full bg-white px-8 pt-6 pb-8 mt-[175px]"
  dir={lang === "AR" ? "rtl" : "ltr"}
>
  {/* LEFT FIXED FILTER SIDEBAR */}
  <aside
  style={{ height: desktopSidebarHeight }}
  className={`
    fixed
    left-8
    top-[210px]
    z-30
    w-[calc(20vw-32px)]
    overflow-y-auto
    pr-4
    border-r border-[#e5e5e5]
    bg-white
    no-scrollbar
    transition-opacity duration-150
    ${desktopSidebarVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
  `}
>
    {/* BREADCRUMB */}
    <ProductsBreadcrumb
  items={breadcrumbItems}
  lang={lang}
  variant="desktop"
/>

    {/* CATEGORIES */}
    <div className="mb-8">
      <h3 className="text-[18px] font-extrabold text-black mb-3">
        {TEXT.category}s
      </h3>

      <div className="space-y-2">
        {visibleDesktopCategories.map((cat) => {
          const active = categoryFilter.includes(cat.key);

          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => {
  setSelectedTag(null);
  setCategoryFilter([cat.key]);
  setInitialSection(cat.key);

  if (desktopCategoryRailRef.current) {
    desktopCategoryRailRef.current.scrollTo({
      left: 0,
      behavior: "smooth",
    });
  }
}}
              className={`
                block w-full text-left text-[13px] uppercase tracking-wide
                ${active ? "font-bold text-black" : "text-[#333]"}
              `}
            >
              {lang === "EN" ? cat.labelEN : cat.labelAR}
            </button>
          );
        })}
      </div>

      {CATEGORY_OPTIONS.length > 5 && (
        <button
          type="button"
          onClick={() => setDesktopCategoriesExpanded((prev) => !prev)}
          className="mt-3 text-[13px] font-semibold text-black"
        >
          {desktopCategoriesExpanded
            ? lang === "EN"
              ? "Show less"
              : "عرض أقل"
            : lang === "EN"
            ? "+ Show more"
            : "+ عرض المزيد"}
        </button>
      )}
    </div>

    {/* FILTERS */}
    <div className="mb-8">
      <h3 className="text-[18px] font-extrabold text-black mb-4">
        {TEXT.filters}
      </h3>

      {/* AVAILABLE */}
      <button
        type="button"
        onClick={() => setOnlyAvailable((prev) => !prev)}
        className="flex items-center gap-3 mb-7"
      >
        <span
          className={`
            relative w-[42px] h-[24px] rounded-full transition
            ${onlyAvailable ? "bg-black" : "bg-[#e5e5e5]"}
          `}
        >
          <span
            className={`
              absolute top-[3px] w-[18px] h-[18px]
              rounded-full bg-white transition
              ${onlyAvailable ? "left-[21px]" : "left-[3px]"}
            `}
          />
        </span>

        <span className="text-[13px] text-black">
          {lang === "EN" ? "Only available" : "المتوفر فقط"}
        </span>
      </button>

      {/* SIZE */}
      <div className="mb-8">
        <h4 className="text-[17px] font-extrabold text-black mb-3">
          {TEXT.size}
        </h4>

        <div className="grid grid-cols-2 gap-x-5 gap-y-2">
          {allSizes.map((size) => {
            const value = String(size);
            const checked = sizeFilter.includes(value);

            return (
              <label
                key={value}
                className="flex items-center gap-2 text-[13px] text-black cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setSizeFilter((prev) =>
                      prev.includes(value)
                        ? prev.filter((x) => x !== value)
                        : [...prev, value]
                    )
                  }
                  className="w-[14px] h-[14px] accent-black"
                />
                <span>{value}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* COLOR */}
      <div className="mb-8">
        <h4 className="text-[17px] font-extrabold text-black mb-3">
          {lang === "EN" ? "Color" : TEXT.color}
        </h4>

        <div className="grid grid-cols-2 gap-x-5 gap-y-2">
          {allColors.map((color) => {
            const checked = colorFilter.includes(color.name);

            return (
              <label
                key={color.name}
                className="flex items-center gap-2 text-[13px] text-black cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setColorFilter((prev) =>
                      prev.includes(color.name)
                        ? prev.filter((x) => x !== color.name)
                        : [...prev, color.name]
                    )
                  }
                  className="w-[14px] h-[14px] accent-black"
                />
                <span>{color.name}</span>
              </label>
            );
          })}
        </div>
      </div>

      {canViewPrices && (
  <>
    {/* PRICE */}
      <div className="mb-8">
        <h4 className="text-[17px] font-extrabold text-black mb-3">
          {TEXT.price}
        </h4>

        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={PRICE_LIMIT}
            value={priceMin}
            onChange={(e) =>
              setPriceMin(Math.min(clampDesktopPrice(e.target.value), priceMax - 1))
            }
            className="
              w-full h-[36px]
              border border-[#e5e5e5]
              bg-[#fff0f3]
              px-2
              text-[12px]
              text-black
              outline-none
            "
          />

          <span className="text-[13px]">-</span>

          <input
            type="number"
            min={0}
            max={PRICE_LIMIT}
            value={priceMax}
            onChange={(e) =>
              setPriceMax(Math.max(clampDesktopPrice(e.target.value), priceMin + 1))
            }
            className="
              w-full h-[36px]
              border border-[#e5e5e5]
              bg-[#fff0f3]
              px-2
              text-[12px]
              text-black
              outline-none
            "
          />
        </div>
      </div>
      </>
      )}
    </div>
  </aside>

  {/* RIGHT CONTENT */}
  <main className="ml-[20%] pl-6 min-w-0">
    {/* DESKTOP TITLE + SORT */}
    <div className="flex items-center justify-between gap-6 mb-4">
      <div className="flex items-center gap-3 min-w-0">
        <h1 className="text-[20px] font-extrabold tracking-[0.18em] uppercase text-black">
          {String(finalTitle || "").toUpperCase()}
        </h1>

        <span className="text-[13px] tracking-[0.08em] text-black whitespace-nowrap">
          {String(productCount)} {TEXT.productsWord}
        </span>
      </div>

      <div className="relative shrink-0">
  <button
    type="button"
    onClick={() => setDesktopSortOpen((prev) => !prev)}
    className="
      h-[42px]
      min-w-[245px]
      border border-[#222]
      bg-white
      px-4
      flex items-center justify-between gap-4
      text-[13px]
      text-black
      shadow-[0_1px_0_rgba(0,0,0,0.04)]
      active:scale-[0.99]
    "
  >
    <span className="flex items-center gap-2 min-w-0">
      <span className="font-bold">
        {lang === "EN" ? "Sorting:" : `${TEXT.sortBy}:`}
      </span>

      <span className="font-normal truncate">
        {getSortLabel()}
      </span>
    </span>

    <SortChevronIcon
      className={`
        w-[17px] h-[17px]
        text-black
        transition-transform duration-200
        ${desktopSortOpen ? "rotate-180" : ""}
      `}
    />
  </button>

  {desktopSortOpen && (
    <div
      className="
        absolute right-0 top-[48px] z-40
        w-full
        bg-white
        border border-[#222]
        shadow-[0_12px_30px_rgba(0,0,0,0.12)]
      "
    >
      <button
        type="button"
        onClick={() => {
          setSortOption("bestSelling");
          setDesktopSortOpen(false);
        }}
        className={`
          w-full px-4 py-3
          text-left text-[13px]
          hover:bg-gray-50
          ${sortOption === "bestSelling" ? "font-bold bg-gray-50" : "font-normal"}
        `}
      >
        {TEXT.bestSelling}
      </button>

      {canViewPrices && (
  <>
    <button
      type="button"
      onClick={() => {
        setSortOption("priceLowHigh");
        setDesktopSortOpen(false);
      }}
      className={`
        w-full px-4 py-3
        text-left text-[13px]
        border-t border-[#e5e5e5]
        hover:bg-gray-50
        ${sortOption === "priceLowHigh" ? "font-bold bg-gray-50" : "font-normal"}
      `}
    >
      {TEXT.priceLowHigh}
    </button>

    <button
      type="button"
      onClick={() => {
        setSortOption("priceHighLow");
        setDesktopSortOpen(false);
      }}
      className={`
        w-full px-4 py-3
        text-left text-[13px]
        border-t border-[#e5e5e5]
        hover:bg-gray-50
        ${sortOption === "priceHighLow" ? "font-bold bg-gray-50" : "font-normal"}
      `}
    >
      {TEXT.priceHighLow}
    </button>
  </>
)}
    </div>
  )}
</div>
    </div>

    {/* DESKTOP FILTER CHIPS */}
    {appliedFilters.length > 0 && (
      <div className="mb-4 flex items-center gap-2 overflow-x-auto no-scrollbar">
        {appliedFilters.map((filter) => (
          <button
            key={filter.id}
            type="button"
            onClick={filter.onClear}
            className="
              shrink-0
              h-[32px]
              px-3
              border border-black
              bg-white
              text-[13px]
              text-black
              flex items-center gap-2
              whitespace-nowrap
            "
          >
            <span>{filter.label}</span>
            <span className="text-[17px] leading-none">×</span>
          </button>
        ))}

        <button
          type="button"
          onClick={clearAllFilters}
          className="
            shrink-0
            h-[32px]
            px-3
            bg-gray-800
            text-[13px]
            text-white
            whitespace-nowrap
          "
        >
          {TEXT.clearAll}
        </button>
      </div>
    )}

    {/* DESKTOP CATEGORY RAIL */}
    <div className="relative mb-7">
      <button
        type="button"
        onClick={() => scrollDesktopCategoryRail("prev")}
        className="
          absolute left-0 top-0 z-10
          h-[38px] w-7
          bg-white/90
          text-black
          text-2xl
          flex items-center justify-center
        "
        aria-label="Previous categories"
      >
        ‹
      </button>

      <div
        ref={desktopCategoryRailRef}
        className="
          flex gap-3
          overflow-x-auto
          scroll-smooth
          no-scrollbar
          px-7
        "
      >
        {isCategorySelectedForTagRow && categoryTagOptions.length > 0
  ? categoryTagOptions.map((item, index) => {
      const active = selectedTag === item.tag;
      const palette = [
        "bg-[#FDE8E8]",
        "bg-[#F7F7D9]",
        "bg-[#F8F5FF]",
        "bg-[#EAF7F1]",
        "bg-[#FFF0D9]",
        "bg-[#EAF2FF]",
      ];

      return (
        <button
          key={item.tag}
          type="button"
          onClick={() =>
            setSelectedTag((prev) => (prev === item.tag ? null : item.tag))
          }
          className={`
            shrink-0
            min-w-[120px]
            h-[38px]
            px-4
            flex items-center justify-center
            text-[13px]
            text-black
            whitespace-nowrap
            ${palette[index % palette.length]}
            ${active ? "font-extrabold" : "font-normal"}
          `}
        >
          {item.tag}
        </button>
      );
    })
  : CATEGORY_OPTIONS.map((c, index) => {
      const palette = [
        "bg-[#FDE8E8]",
        "bg-[#F7F7D9]",
        "bg-[#F8F5FF]",
        "bg-[#EAF7F1]",
        "bg-[#FFF0D9]",
        "bg-[#EAF2FF]",
      ];

      return (
        <button
          key={c.key}
          type="button"
          onClick={() => {
            setSelectedTag(null);
            setCategoryFilter([c.key]);
            setInitialSection(c.key);
          }}
          className={`
            shrink-0
            min-w-[120px]
            h-[38px]
            px-4
            flex items-center justify-center
            text-[13px]
            text-black
            whitespace-nowrap
            ${palette[index % palette.length]}
          `}
        >
          {lang === "EN" ? c.labelEN : c.labelAR}
        </button>
      );
    })}
      </div>

      <button
        type="button"
        onClick={() => scrollDesktopCategoryRail("next")}
        className="
          absolute right-0 top-0 z-10
          h-[38px] w-7
          bg-white/90
          text-black
          text-2xl
          flex items-center justify-center
        "
        aria-label="Next categories"
      >
        ›
      </button>
    </div>

    {/* DESKTOP PRODUCTS GRID / EMPTY STATE */}
{filteredAndSorted.length === 0 ? (
  <ProductEmptyState
    lang={lang}
    text={TEXT.noProducts}
    onClear={clearAllFilters}
    onShopAll={() => navigate("/all-products")}
  />
) : (
  <div className="grid grid-cols-3 gap-x-6 gap-y-9">
    {isDesktopView &&
      paginatedProducts.map((p, index) => (
        <ProductCard
          key={p.id}
          product={p}
          imagePriority={index < 6}
        />
      ))}
  </div>
)}
  </main>
</div>

            {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="md:hidden w-full bg-white px-4 pb-8">
          <div className="border-t border-[#D9D9D9] pt-4">
            <div className="flex justify-center items-center">
<div
  dir="ltr"
  className="inline-flex border border-[#D9D9D9] bg-white"
>
  {/* PREVIOUS PAGE */}
  <button
    type="button"
    onClick={() => goToPage(currentPage - 1)}
    disabled={currentPage === 1}
    aria-label={
      lang === "AR" ? "الصفحة السابقة" : "Previous page"
    }
    className="
      w-11 h-11
      flex items-center justify-center
      border-r border-[#D9D9D9]
      bg-white
      text-[24px] text-black
      transition
      hover:bg-gray-50
      active:bg-gray-100
      disabled:text-gray-300
      disabled:bg-gray-50
      disabled:cursor-not-allowed
    "
  >
    ‹
  </button>

  {paginationItems.map((item, index) => {
    if (item === "...") {
      return (
        <span
          key={`dots-${index}`}
          className="
            w-11 h-11
            flex items-center justify-center
            border-r border-[#D9D9D9]
            text-[15px] text-black
          "
        >
          ...
        </span>
      );
    }

    return (
      <button
        key={item}
        type="button"
        onClick={() => goToPage(item)}
        aria-label={`${
          lang === "AR" ? "الانتقال إلى الصفحة" : "Go to page"
        } ${item}`}
        aria-current={currentPage === item ? "page" : undefined}
        className={`
          w-11 h-11
          flex items-center justify-center
          border-r border-[#D9D9D9]
          text-[15px]
          transition
          ${
            currentPage === item
              ? "bg-[#1E1E1E] text-white"
              : "bg-white text-black hover:bg-gray-50"
          }
        `}
      >
        {item}
      </button>
    );
  })}

  {/* NEXT PAGE */}
  <button
    type="button"
    onClick={() => goToPage(currentPage + 1)}
    disabled={currentPage === totalPages}
    aria-label={
      lang === "AR" ? "الصفحة التالية" : "Next page"
    }
    className="
      w-11 h-11
      flex items-center justify-center
      bg-white
      text-[24px] text-black
      transition
      hover:bg-gray-50
      active:bg-gray-100
      disabled:text-gray-300
      disabled:bg-gray-50
      disabled:cursor-not-allowed
    "
  >
    ›
  </button>
</div>
            </div>

            <p className="text-center mt-5 text-[17px] text-black">
              {lang === "EN" ? "Showing" : "عرض"}{" "}
              {(currentPage - 1) * PAGE_SIZE + 1} -{" "}
              {Math.min(currentPage * PAGE_SIZE, productCount)}{" "}
              {lang === "EN" ? "of" : "من"} {productCount}{" "}
              {lang === "EN" ? "items" : "منتج"}
            </p>
          </div>
        </div>
      )}

{/* DESKTOP PAGINATION */}
{totalPages > 1 && (
  <div className="hidden md:block w-full bg-white px-8 pb-10">
    <div className="ml-[20%] pl-6 border-t border-[#D9D9D9] pt-5">
      <div className="flex items-center justify-between gap-6">
        <p className="text-[13px] text-black">
          {lang === "EN" ? "Showing" : "عرض"}{" "}
          {(currentPage - 1) * PAGE_SIZE + 1} -{" "}
          {Math.min(currentPage * PAGE_SIZE, productCount)}{" "}
          {lang === "EN" ? "of" : "من"} {productCount}{" "}
          {lang === "EN" ? "items" : "منتج"}
        </p>

        <div
  dir="ltr"
  className="inline-flex border border-[#D9D9D9] bg-white"
>
  {/* PREVIOUS PAGE */}
  <button
    type="button"
    onClick={() => goToPage(currentPage - 1)}
    disabled={currentPage === 1}
    aria-label={
      lang === "AR" ? "الصفحة السابقة" : "Previous page"
    }
    className="
      w-11 h-11
      flex items-center justify-center
      border-r border-[#D9D9D9]
      bg-white
      text-[24px] text-black
      transition
      hover:bg-gray-50
      active:bg-gray-100
      disabled:text-gray-300
      disabled:bg-gray-50
      disabled:cursor-not-allowed
    "
  >
    ‹
  </button>

  {paginationItems.map((item, index) => {
    if (item === "...") {
      return (
        <span
          key={`desktop-dots-${index}`}
          className="
            w-11 h-11
            flex items-center justify-center
            border-r border-[#D9D9D9]
            text-[14px] text-black
          "
        >
          ...
        </span>
      );
    }

    return (
      <button
        key={`desktop-${item}`}
        type="button"
        onClick={() => goToPage(item)}
        aria-label={`${
          lang === "AR" ? "الانتقال إلى الصفحة" : "Go to page"
        } ${item}`}
        aria-current={currentPage === item ? "page" : undefined}
        className={`
          w-11 h-11
          flex items-center justify-center
          border-r border-[#D9D9D9]
          text-[14px]
          transition
          ${
            currentPage === item
              ? "bg-[#1E1E1E] text-white"
              : "bg-white text-black hover:bg-gray-50"
          }
        `}
      >
        {item}
      </button>
    );
  })}

  {/* NEXT PAGE */}
  <button
    type="button"
    onClick={() => goToPage(currentPage + 1)}
    disabled={currentPage === totalPages}
    aria-label={
      lang === "AR" ? "الصفحة التالية" : "Next page"
    }
    className="
      w-11 h-11
      flex items-center justify-center
      bg-white
      text-[24px] text-black
      transition
      hover:bg-gray-50
      active:bg-gray-100
      disabled:text-gray-300
      disabled:bg-gray-50
      disabled:cursor-not-allowed
    "
  >
    ›
  </button>
</div>
      </div>
    </div>
  </div>
)}

<ProductsPageFeaturedSection
  section={productsPageFeatured}
  lang={lang}
  navigate={navigate}
  resolveProductImage={resolveProductImage}
  saveProductReturnPosition={saveProductReturnPosition}
  canViewPrices={canViewPrices}
/>

      {/* MOBILE FILTER DRAWER - RIGHT SIDE */}
<div
  className={`
    fixed inset-0 z-[9999]
    transition-opacity duration-300
    ${isFilterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
  `}
>
  <div
    className="absolute inset-0 bg-black/45"
    onClick={() => setIsFilterOpen(false)}
  />

  <div
    className={`
      absolute top-0 bottom-0 right-0
      w-[70vw] max-w-[430px]
      bg-white
      shadow-[0_20px_60px_rgba(0,0,0,0.28)]
      transform transition-transform duration-300
      ${isFilterOpen ? "translate-x-0" : "translate-x-full"}
    `}
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <button
      type="button"
      onClick={() => setIsFilterOpen(false)}
      className="
        absolute right-5 top-8 z-20
        text-[42px] leading-none
        font-black text-black
        active:scale-95
      "
      aria-label="Close filters"
    >
      ×
    </button>

    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto px-6 pt-20 pb-32">
        {/* ONLY AVAILABLE */}
        <div className="border-b border-[#d9d9d9] pb-7">
          <h3 className="text-[18px] font-extrabold mb-5">
            {lang === "EN" ? "Use filters:" : "استخدام الفلاتر:"}
          </h3>

          <button
            type="button"
            onClick={() => setOnlyAvailable((prev) => !prev)}
            className="flex items-center gap-4"
          >
            <span
              className={`
                relative w-[56px] h-[32px] rounded-full transition
                ${onlyAvailable ? "bg-black" : "bg-[#dedede]"}
              `}
            >
              <span
                className={`
                  absolute top-[3px] w-[26px] h-[26px]
                  rounded-full bg-white transition
                  ${onlyAvailable ? "left-[27px]" : "left-[3px]"}
                `}
              />
            </span>

            <span className="text-[16px] text-black">
              {lang === "EN" ? "Only available" : "المتوفر فقط"}
            </span>
          </button>
        </div>

        {/* SIZE */}
        <FilterDrawerSection
          title={TEXT.size}
          open={sizeSectionOpen}
          setOpen={setSizeSectionOpen}
        >
          <div className="space-y-4 pt-4">
            {allSizes.map((size) => {
              const value = String(size);
              const checked = sizeFilter.includes(value);

              return (
                <label
                  key={value}
                  className="flex items-center gap-3 text-[16px] text-black"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setSizeFilter((prev) =>
                        prev.includes(value)
                          ? prev.filter((x) => x !== value)
                          : [...prev, value]
                      )
                    }
                    className="
                      appearance-none
                      w-[22px] h-[22px]
                      rounded-[6px]
                      border border-[#9a9a9a]
                      bg-white
                      checked:bg-black
                      checked:border-black
                    "
                  />

                  <span>{value}</span>
                </label>
              );
            })}
          </div>
        </FilterDrawerSection>

        {/* COLOUR */}
        <FilterDrawerSection
          title={lang === "EN" ? "Colour" : TEXT.color}
          open={colorSectionOpen}
          setOpen={setColorSectionOpen}
        >
          <div className="space-y-4 pt-4">
            {allColors.map((color) => {
              const checked = colorFilter.includes(color.name);

              return (
                <label
                  key={color.name}
                  className="flex items-center gap-3 text-[16px] text-black"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setColorFilter((prev) =>
                        prev.includes(color.name)
                          ? prev.filter((x) => x !== color.name)
                          : [...prev, color.name]
                      )
                    }
                    className="
                      appearance-none
                      w-[22px] h-[22px]
                      rounded-[6px]
                      border border-[#9a9a9a]
                      bg-white
                      checked:bg-black
                      checked:border-black
                    "
                  />

                  <span>{color.name}</span>
                </label>
              );
            })}
          </div>
        </FilterDrawerSection>

{canViewPrices && (
        <FilterDrawerSection
          title={TEXT.price}
          open={priceSectionOpen}
          setOpen={setPriceSectionOpen}
        >
          <div className="pt-6">
 <div
  ref={priceSliderRef}
  className="relative h-14 mx-1 select-none touch-none"
  onMouseDown={(e) => {
    const slider = priceSliderRef.current;
    if (!slider) return;

    const rect = slider.getBoundingClientRect();
    const clickPercent = Math.min(
      1,
      Math.max(0, (e.clientX - rect.left) / rect.width)
    );
    const clickValue = Math.round(clickPercent * PRICE_LIMIT);

    const distanceToMin = Math.abs(clickValue - priceMin);
    const distanceToMax = Math.abs(clickValue - priceMax);
    const thumb = distanceToMin <= distanceToMax ? "min" : "max";

    setDraggingPriceThumb(thumb);
    updatePriceFromPointer(e.clientX, thumb);
  }}
  onTouchStart={(e) => {
    const slider = priceSliderRef.current;
    if (!slider) return;

    const touch = e.touches[0];
    const rect = slider.getBoundingClientRect();
    const clickPercent = Math.min(
      1,
      Math.max(0, (touch.clientX - rect.left) / rect.width)
    );
    const clickValue = Math.round(clickPercent * PRICE_LIMIT);

    const distanceToMin = Math.abs(clickValue - priceMin);
    const distanceToMax = Math.abs(clickValue - priceMax);
    const thumb = distanceToMin <= distanceToMax ? "min" : "max";

    setDraggingPriceThumb(thumb);
    updatePriceFromPointer(touch.clientX, thumb);
  }}
>
  {/* base line */}
  <div className="absolute left-0 right-0 top-1/2 h-[4px] -translate-y-1/2 bg-[#d9d9d9] rounded-full" />

  {/* active line */}
  <div
    className="absolute top-1/2 h-[5px] -translate-y-1/2 bg-black rounded-full"
    style={{
      left: `${(priceMin / PRICE_LIMIT) * 100}%`,
      width: `${((priceMax - priceMin) / PRICE_LIMIT) * 100}%`,
    }}
  />

  {/* MIN THUMB */}
  <button
    type="button"
    onMouseDown={(e) => {
      e.stopPropagation();
      setDraggingPriceThumb("min");
    }}
    onTouchStart={(e) => {
      e.stopPropagation();
      setDraggingPriceThumb("min");
    }}
    className="
      absolute top-1/2
      w-[24px] h-[24px]
      -translate-x-1/2 -translate-y-1/2
      rounded-full
      bg-black
      border-2 border-white
      shadow-[0_2px_8px_rgba(0,0,0,0.25)]
      active:scale-110
      transition
    "
    style={{
      left: `${(priceMin / PRICE_LIMIT) * 100}%`,
    }}
    aria-label="Minimum price"
  />

  {/* MAX THUMB */}
  <button
    type="button"
    onMouseDown={(e) => {
      e.stopPropagation();
      setDraggingPriceThumb("max");
    }}
    onTouchStart={(e) => {
      e.stopPropagation();
      setDraggingPriceThumb("max");
    }}
    className="
      absolute top-1/2
      w-[24px] h-[24px]
      -translate-x-1/2 -translate-y-1/2
      rounded-full
      bg-black
      border-2 border-white
      shadow-[0_2px_8px_rgba(0,0,0,0.25)]
      active:scale-110
      transition
    "
    style={{
      left: `${(priceMax / PRICE_LIMIT) * 100}%`,
    }}
    aria-label="Maximum price"
  />
</div>

  <div className="mt-4 flex items-center justify-between gap-3">
    <div className="flex-1 h-[52px] bg-[#fff0f3] flex items-center justify-between px-3">
      <span className="text-[15px] text-black">
        {Number(priceMin || 0).toFixed(2)}
      </span>
      <span className="text-[14px] text-black">USD</span>
    </div>

    <span className="text-[18px]">-</span>

    <div className="flex-1 h-[52px] bg-[#fff0f3] flex items-center justify-between px-3">
      <span className="text-[15px] text-black">
        {Number(priceMax || PRICE_LIMIT).toFixed(2)}
      </span>
      <span className="text-[14px] text-black">USD</span>
    </div>
  </div>
</div>
        </FilterDrawerSection>
)}
        {/* SORT */}
        <div className="border-b border-[#d9d9d9] py-6">
          <h3 className="text-[18px] font-extrabold mb-4">
            {TEXT.sortBy}:
          </h3>

          <div className="relative">
            <button
              type="button"
              onClick={() => setIsSortOpen((prev) => !prev)}
              className="
                h-[44px] w-full
                bg-white
                flex items-center justify-between
                text-[16px] text-black
              "
            >
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {getSortLabel()}
              </span>

              <SortChevronIcon
                className={`
                  w-[18px] h-[18px]
                  text-black
                  transition-transform duration-200
                  ${isSortOpen ? "rotate-180" : ""}
                `}
              />
            </button>

           {isSortOpen && (
  <div
    className="
      relative
      mt-2
      w-full
      bg-white
      border border-[#d9d9d9]
      shadow-lg
      z-30
      overflow-hidden
    "
  >
    <button
      onClick={() => {
        setSortOption("bestSelling");
        setIsSortOpen(false);
      }}
      className="w-full px-4 py-3 text-left text-[15px] hover:bg-gray-50"
    >
      {TEXT.bestSelling}
    </button>

    {canViewPrices && (
      <>
        <button
          onClick={() => {
            setSortOption("priceLowHigh");
            setIsSortOpen(false);
          }}
          className="w-full px-4 py-3 text-left text-[15px] hover:bg-gray-50"
        >
          {TEXT.priceLowHigh}
        </button>

        <button
          onClick={() => {
            setSortOption("priceHighLow");
            setIsSortOpen(false);
          }}
          className="w-full px-4 py-3 text-left text-[15px] hover:bg-gray-50"
        >
          {TEXT.priceHighLow}
        </button>
      </>
    )}
  </div>
)}
          </div>
        </div>
      </div>

      {/* STICKY USE FILTERS BUTTON */}
      <div className="absolute bottom-0 left-0 right-0 bg-white px-0 pb-0 pt-3">
        <button
          type="button"
          onClick={() => setIsFilterOpen(false)}
          className="
            w-full h-[66px]
            bg-black text-white
            text-[17px] font-extrabold
            tracking-[0.08em]
            uppercase
            active:scale-[0.99]
          "
        >
          {lang === "EN" ? "Use filters" : "استخدم الفلاتر"}
        </button>
      </div>
    </div>
  </div>
</div>

      {/* SCROLL TOP - same as SearchOverlay */}
{showScrollTop && !isDesktopView && (
  <button
    type="button"
    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    className="
      fixed right-6 bottom-8 z-[9999]
      w-[62px] h-[62px]
      rounded-full
      bg-black text-white
      flex items-center justify-center
      shadow-[0_10px_28px_rgba(0,0,0,0.28)]
      active:scale-95
      transition
    "
    aria-label="Scroll to top"
    title="Scroll to top"
  >
    <ScrollTopArrowIcon className="w-[34px] h-[34px] text-white" />
  </button>
)}

      <Footer className="mt-auto" />
    </div>
  );
}

function ProductsBreadcrumb({ items, lang, variant = "mobile" }) {
  const isMobile = variant === "mobile";

  return (
    <div
      dir={lang === "AR" ? "rtl" : "ltr"}
      className={`
        relative
        ${isMobile ? "md:hidden text-[14px] mb-4" : "text-[14px] font-medium mb-6"}
        text-black
        overflow-hidden
      `}
    >
      <div
        className="
          flex items-center gap-2
          overflow-x-auto
          no-scrollbar
          whitespace-nowrap
          pr-10
        "
      >
        {items.map((item, index) => (
          <React.Fragment key={item.id}>
            {index > 0 && <span className="text-[#555]">›</span>}

            <button
              type="button"
              onClick={item.onClick}
              className={`
                shrink-0
                hover:underline
                ${index === items.length - 1 ? "font-semibold text-black" : "text-[#111]"}
              `}
            >
              {item.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      {isMobile && (
        <div
          className={`
            pointer-events-none
            absolute top-0 bottom-0
            w-12
            ${lang === "AR" ? "left-0" : "right-0"}
          `}
          style={{
            background:
              lang === "AR"
                ? "linear-gradient(to right, white, rgba(255,255,255,0))"
                : "linear-gradient(to left, white, rgba(255,255,255,0))",
          }}
        />
      )}
    </div>
  );
}

function ProductsPageFeaturedSection({
  section,
  lang,
  navigate,
  resolveProductImage,
  saveProductReturnPosition,
  canViewPrices,
}) {
  const railRef = useRef(null);

  const products = Array.isArray(section?.products) ? section.products : [];

  if (!section || products.length === 0) return null;

  const title = lang === "AR" ? section.title_ar : section.title_en;

  const scrollRail = (direction) => {
    if (!railRef.current) return;

    railRef.current.scrollBy({
      left: direction === "next" ? 260 : -260,
      behavior: "smooth",
    });
  };

  const getProductName = (product) =>
    lang === "AR" ? product.name_ar : product.name_en;

  const getProductPricing = (product) => {
  const base = Number(product.base_price || product.price || 0);
  const salePercent = Number(product.sale_percent || product.discount || 0);
  const hasDiscount = Boolean(product.on_sale) && salePercent > 0;

  const finalPrice = hasDiscount
    ? base * (1 - salePercent / 100)
    : base;

  return {
    base,
    finalPrice,
    hasDiscount,
  };
};

  const resolveMediaUrl = (media) => {
  if (!media) return "/images/placeholder-product.jpg";

  if (typeof media === "string") {
    return resolveProductImage(media);
  }

  return resolveProductImage(
    media.url || media.image || media.image_url || ""
  );
};

const isVideoMedia = (media) => {
  const url = resolveMediaUrl(media);
  const type = typeof media === "object" ? media.media_type : "";

  return type === "video" || /\.(mp4|webm|ogg|mov)$/i.test(url.split("?")[0]);
};

const getProductSlug = (product) => {
  return (
    product?.seo_slug ||
    product?.slug ||
    product?.product_slug ||
    ""
  );
};

const goToFeaturedProduct = (product, element) => {
  const slug = getProductSlug(product);
  if (!slug) return;

  saveProductReturnPosition({
    productId: product.id,
    element,
  });

  navigate(`/product/${encodeURIComponent(slug)}`);
};

const getFirstMedia = (product) => {
  return (
    (Array.isArray(product.images) && product.images.length
      ? product.images[0]
      : null) ||
    product.image ||
    product.image_url ||
    "/images/placeholder-product.jpg"
  );
};

  return (
    <section
  className="w-full bg-white px-4 pb-8 md:px-8 md:pb-12"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <div className="pt-2 md:ml-[20%] md:pl-6">
        <h2
          className="
            text-[24px] md:text-[30px]
            leading-none
            font-semibold
            uppercase
            tracking-[0.12em]
            text-[#ff9f00]
          "
        >
          {title}
        </h2>

        <div className="mt-3 h-[2px] w-full bg-[#ff9f00]" />

        <div className="relative mt-5">
          <button
            type="button"
            onClick={() => scrollRail("prev")}
            className="
              absolute -left-1 top-[38%] z-20
              w-[30px] h-[44px]
              bg-white/85
              text-black
              text-[26px]
              flex items-center justify-center
              shadow-[0_4px_14px_rgba(0,0,0,0.08)]
              active:scale-95
            "
            aria-label="Previous products"
          >
            ‹
          </button>

          <div
            ref={railRef}
            className="
              flex gap-4
              overflow-x-auto
              scroll-smooth
              no-scrollbar
            "
          >
         {products.map((product) => {
  const firstMedia = getFirstMedia(product);
  const mediaUrl = resolveMediaUrl(firstMedia);
  const mediaIsVideo = isVideoMedia(firstMedia);
  const pricing = getProductPricing(product);

  return (
    <button
  key={product.id}
  type="button"
  onClick={(e) => goToFeaturedProduct(product, e.currentTarget)}
  data-product-card-id={product.id}
  disabled={!getProductSlug(product)}
  className="
    shrink-0
    w-[calc((100vw-48px)/2)]
    md:w-[260px]
    max-w-[260px]
    min-h-[430px]
    md:min-h-[560px]
    text-center
    bg-white
    flex flex-col
    disabled:opacity-60
    disabled:cursor-not-allowed
  "
>
      <div
        className="
          w-full
          h-[285px]
          md:h-[390px]
          bg-[#f7f7f7]
          overflow-hidden
        "
      >
        {mediaIsVideo ? (
  <video
  src={mediaUrl}
  muted
  loop
  autoPlay
  playsInline
  preload="metadata"
  className="w-full h-full object-cover block"
/>
        ) : (
  <img
  src={mediaUrl}
  alt={getProductName(product)}
  className="w-full h-full object-cover block"
  loading="lazy"
  decoding="async"
/>
        )}
      </div>

      <div className="flex flex-col flex-1">
        <h3
          className="
            mt-4
            px-1
            text-[18px] md:text-[20px]
            leading-[1.25]
            font-normal
            text-black
          "
        >
          {getProductName(product)}
        </h3>

        <div className="mt-2 flex items-center justify-center gap-2 text-[15px] md:text-[16px] text-black">
  {canViewPrices ? (
    <>
      <span className="font-normal text-black">
        ${pricing.finalPrice.toFixed(2)}
      </span>

      {pricing.hasDiscount && (
        <span className="font-normal text-black line-through">
          ${pricing.base.toFixed(2)}
        </span>
      )}
    </>
  ) : (
    <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-wide text-black">
      {lang === "AR" ? "سجّل الدخول لمشاهدة السعر" : "Login to view price"}
    </span>
  )}
</div>
      </div>
    </button>
  );
})}
          </div>

          <button
            type="button"
            onClick={() => scrollRail("next")}
            className="
              absolute -right-1 top-[38%] z-20
              w-[30px] h-[44px]
              bg-white/85
              text-black
              text-[26px]
              flex items-center justify-center
              shadow-[0_4px_14px_rgba(0,0,0,0.08)]
              active:scale-95
            "
            aria-label="Next products"
          >
            ›
          </button>
        </div>

        <div className="mt-0 h-[2px] w-full bg-[#ff9f00]" />
      </div>
    </section>
  );
}

/**
 * Small helper component for collapsible sections inside the filter drawer.
 */
function FilterDrawerSection({ title, open, setOpen, children }) {
  return (
    <div className="border-b border-[#d9d9d9] py-6">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center gap-2 text-[18px] font-extrabold text-black"
      >
        <span>{title}</span>

        <SortChevronIcon
          className={`
            w-[18px] h-[18px]
            text-black
            transition-transform duration-200
            ${open ? "rotate-180" : ""}
          `}
        />
      </button>

      {open && children}
    </div>
  );
}

export default ProductsPage;
